/**
 * Application principale BTPal
 * Routing et providers
 */

import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider, useAuth, ThemeProvider } from '@/hooks';
import { useBackendHealth } from '@/hooks/useBackendHealth';
import { Layout } from '@/components/layout/Layout';
import { ToastProvider } from '@/components/ui/toast';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { CookieBanner } from '@/components/CookieBanner';
import { MaintenancePage } from '@/components/MaintenancePage';

// Lazy-loaded pages for code splitting
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/RegisterPage'));
const VerifyEmailPage = lazy(() => import('@/pages/VerifyEmailPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));
const QuoteAcceptPage = lazy(() => import('@/pages/QuoteAcceptPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const ClientsPage = lazy(() => import('@/pages/ClientsPage'));
const QuotesPage = lazy(() => import('@/pages/QuotesPage'));
const InvoicesPage = lazy(() => import('@/pages/InvoicesPage'));
const ExpensesPage = lazy(() => import('@/pages/ExpensesPage'));
const ProjectsPage = lazy(() => import('@/pages/ProjectsPage'));
const SimulatorPage = lazy(() => import('@/pages/SimulatorPage'));
const CataloguePage = lazy(() => import('@/pages/CataloguePage'));
const SubcontractorsPage = lazy(() => import('@/pages/SubcontractorsPage'));
const RelancesPage = lazy(() => import('@/pages/RelancesPage'));
const BankPage = lazy(() => import('@/pages/BankPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const WelcomePage = lazy(() => import('@/pages/WelcomePage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const MentionsLegalesPage = lazy(() => import('@/pages/MentionsLegalesPage'));
const CguPage = lazy(() => import('@/pages/CguPage'));
const PolitiqueConfidentialitePage = lazy(() => import('@/pages/PolitiqueConfidentialitePage'));

// ========================================
// Query Client
// ========================================

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

// ========================================
// Loading Fallback for Suspense
// ========================================

function LoadingFallback(): ReactNode {
  return (
    <div className="flex items-center justify-center py-12">
      <img src="/logo.png" alt="BTPal" className="w-8 h-8 rounded-lg animate-pulse" />
    </div>
  );
}

// ========================================
// Protected Route Component
// ========================================

function ProtectedRoute({ children }: { children: ReactNode }): ReactNode {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <img src="/logo.png" alt="BTPal" className="w-10 h-10 rounded-md animate-pulse" />
          <p className="text-[#64748B]">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
}

// ========================================
// Public Route Component
// ========================================

function PublicRoute({ children }: { children: ReactNode }): ReactNode {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
<img src="/logo.png" alt="BTPal" className="w-10 h-10 rounded-md animate-pulse" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// ========================================
// App Routes
// ========================================

function AppRoutes(): ReactNode {
  const { isDown } = useBackendHealth();
  if (isDown) return <MaintenancePage />;
  return (
    <Routes>
      {/* Fully public routes (no auth check) */}
      <Route
        path="/devis/accepter/:token"
        element={
          <Suspense fallback={<LoadingFallback />}><QuoteAcceptPage /></Suspense>
        }
      />

      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Suspense fallback={<LoadingFallback />}><LoginPage /></Suspense>
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Suspense fallback={<LoadingFallback />}><RegisterPage /></Suspense>
          </PublicRoute>
        }
      />
      <Route
        path="/verify-email"
        element={<Suspense fallback={<LoadingFallback />}><VerifyEmailPage /></Suspense>}
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <Suspense fallback={<LoadingFallback />}><ForgotPasswordPage /></Suspense>
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={<Suspense fallback={<LoadingFallback />}><ResetPasswordPage /></Suspense>}
      />
      <Route
        path="/welcome"
        element={<Suspense fallback={<LoadingFallback />}><WelcomePage /></Suspense>}
      />

      {/* Protected routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}><DashboardPage /></Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}><ClientsPage /></Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/quotes"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}><QuotesPage /></Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}><InvoicesPage /></Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/expenses"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}><ExpensesPage /></Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}><ProjectsPage /></Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/simulator"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}><SimulatorPage /></Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/catalogue"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}><CataloguePage /></Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subcontractors"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}><SubcontractorsPage /></Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bank"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}><BankPage /></Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/relances"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}><RelancesPage /></Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}><SettingsPage /></Suspense>
          </ProtectedRoute>
        }
      />

      {/* Pages légales — publiques */}
      <Route path="/mentions-legales" element={<Suspense fallback={<LoadingFallback />}><MentionsLegalesPage /></Suspense>} />
      <Route path="/cgu" element={<Suspense fallback={<LoadingFallback />}><CguPage /></Suspense>} />
      <Route path="/politique-confidentialite" element={<Suspense fallback={<LoadingFallback />}><PolitiqueConfidentialitePage /></Suspense>} />

      {/* Redirect root to dashboard */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      {/* 404 */}
      <Route
        path="*"
        element={
          <Suspense fallback={<LoadingFallback />}>
            <NotFoundPage />
          </Suspense>
        }
      />
    </Routes>
  );
}

// ========================================
// Main App Component
// ========================================

function App(): ReactNode {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ErrorBoundary>
          <BrowserRouter>
            <AuthProvider>
              <ToastProvider>
                <AppRoutes />
                <CookieBanner />
              </ToastProvider>
            </AuthProvider>
          </BrowserRouter>
        </ErrorBoundary>
      </ThemeProvider>
      {/* React Query Devtools - uniquement en développement */}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

export default App;
